/**
 * The level's moving parts, owned in one place.
 *
 * `@shared/movers` says where a primitive *is* at time `t`; this class owns the
 * `t`, poses the live primitives once per substep, and answers the three
 * questions the rest of the client asks about a thing that moves:
 *
 * - how fast is its surface travelling here ({@link MoverSystem.velocityAt}) —
 *   the physics resolves every contact in the surface's own frame;
 * - where did it carry a point this tick ({@link MoverSystem.carryPoint},
 *   {@link MoverSystem.deltaRotation}) — a planted body rides a platform by the
 *   platform's *exact* delta transform, so there is no drift to accumulate;
 * - is it solid at all right now ({@link MoverSystem.isActive}) — a `blink`
 *   platform spends part of every cycle gone, and the broadphase must not hand
 *   the narrow phase something that is not there.
 *
 * **The clock is wall clock, not simulation time.** Motion never travels over
 * the wire: every client evaluates the same pure functions and has to agree, so
 * the phase is anchored to `Date.now()` rather than to however many substeps
 * this tab happened to run. It is *integrated* by `dt` and only corrected toward
 * the wall — an exponential approach with `DRIFT_TAU`, or an outright snap past
 * `DRIFT_SNAP` — because a raw `Date.now()` every substep would jitter the
 * platform velocities by whatever the timer's resolution is.
 *
 * **Nothing here mutates a primitive outside {@link MoverSystem.tick}.** The
 * level hash is computed from rest poses, so it must be taken before this class
 * is constructed, and the poses it sees must never depend on when the tab
 * started.
 */

import { Quaternion, Vector3 } from 'three';
import type { MoverSpec } from '@shared/movers';
import {
  applyMover,
  clonePrimitive,
  moverActiveAt,
  moverAngleAt,
  moverOffsetAt,
  moverSweptAabb,
  moverVelocityAt,
} from '@shared/movers';
import type { LevelData, Primitive, V3 } from '@shared/primitives';
import { TUNING } from './pogo';

/** Forwarded so the render layer reads blink state through this module. */
export { blinkStateAt, type BlinkState } from '@shared/movers';

/**
 * Epoch the level clock counts from, in ms. Seconds since mid-2025 leave a
 * double with microsecond resolution; seconds since 1970 do not, and the phase
 * of a 4 s platform is exactly the place that would show.
 */
const EPOCH_MS = 1_750_000_000_000;

/** Time constant of the drift correction toward the wall clock, seconds. */
const DRIFT_TAU = 0.5;
/** Past this the clock did not drift, the tab was suspended — snap instead. */
const DRIFT_SNAP = 1;

/** Milliseconds since {@link EPOCH_MS}. The shape an injected clock must have. */
function defaultNow(): number {
  return Date.now() - EPOCH_MS;
}

/** The swept box a moving primitive is registered under in the broadphase. */
export interface MoverBounds {
  readonly prim: Primitive;
  readonly min: V3;
  readonly max: V3;
}

/** Rest pose and spec, for a consumer that poses the primitive itself. */
export interface MoverEntry {
  readonly prim: Primitive;
  readonly base: Primitive;
  readonly spec: MoverSpec;
}

/** Everything time-varying about one primitive, including this tick's delta. */
interface Entry extends MoverBounds, MoverEntry {
  active: boolean;
  /** Rotation angle at the previous / current tick, radians. */
  prevAngle: number;
  curAngle: number;
  /** Translation at the previous / current tick, metres. */
  readonly prevOffset: V3;
  readonly curOffset: V3;
}

// --- module scratch (single-threaded, never re-entered) ---------------------

const _pt: V3 = [0, 0, 0];
const _v: V3 = [0, 0, 0];
const _axis = new Vector3();
const _pivot = new Vector3();
const _quat = new Quaternion();

/**
 * Every mover in a level, ticked as one.
 *
 * Construct **after** the level hash is taken, and tick it *before* the physics
 * every substep: the body resolves against the pose the platform holds now.
 */
export class MoverSystem {
  private readonly entries: Entry[] = [];
  private readonly byPrim = new Map<Primitive, Entry>();
  private readonly movingPrims: Primitive[] = [];
  private readonly nowMs: () => number;
  private clock: number;

  /**
   * @param now injectable clock returning **milliseconds since {@link EPOCH_MS}**
   *   — the self-tests drive a deterministic one in lockstep with their substeps.
   *   Defaults to wall clock.
   */
  constructor(level: LevelData, now?: () => number) {
    this.nowMs = now ?? defaultNow;
    this.clock = this.nowMs() / 1000;

    const t = this.clock;
    for (const prim of level.primitives) {
      const spec = prim.mover;
      if (spec === undefined) continue;
      // The base is the rest pose the generator emitted. `applyMover` always
      // transforms it afresh, so a pose can never accumulate error.
      const base = clonePrimitive(prim);
      const box = moverSweptAabb(base, spec);
      const angle = moverAngleAt(spec, t);
      const entry: Entry = {
        prim,
        base,
        spec,
        min: box.min,
        max: box.max,
        active: moverActiveAt(spec, t),
        prevAngle: angle,
        curAngle: angle,
        prevOffset: moverOffsetAt(spec, t, [0, 0, 0]),
        curOffset: moverOffsetAt(spec, t, [0, 0, 0]),
      };
      this.entries.push(entry);
      this.movingPrims.push(prim);
      this.byPrim.set(prim, entry);
    }
  }

  /** Seconds on the shared level clock. Every mover is a pure function of it. */
  get time(): number {
    return this.clock;
  }

  /** Number of primitives that move. */
  get count(): number {
    return this.entries.length;
  }

  /**
   * Advance the clock by one substep and re-pose every mover.
   *
   * The per-entry `prev → cur` bookkeeping is what {@link carryPoint} and
   * {@link deltaRotation} hand a rider, so it has to be updated in the same pass
   * that moves the geometry.
   */
  tick(dt: number): void {
    this.clock += dt;
    const drift = this.nowMs() / 1000 - this.clock;
    if (drift > DRIFT_SNAP || drift < -DRIFT_SNAP) this.clock += drift;
    else this.clock += drift * Math.min(1, dt / DRIFT_TAU);

    const t = this.clock;
    for (let i = 0; i < this.entries.length; i++) {
      const e = this.entries[i]!;
      e.prevAngle = e.curAngle;
      e.prevOffset[0] = e.curOffset[0];
      e.prevOffset[1] = e.curOffset[1];
      e.prevOffset[2] = e.curOffset[2];
      e.curAngle = moverAngleAt(e.spec, t);
      moverOffsetAt(e.spec, t, e.curOffset);
      applyMover(e.base, e.spec, t, e.prim);
      e.active = moverActiveAt(e.spec, t);
    }
  }

  // --- physics -------------------------------------------------------------

  /**
   * World velocity of `prim`'s surface at `point`, m/s. Zero for anything that
   * does not move, so callers never need to branch.
   *
   * @returns `out`.
   */
  velocityAt(prim: Primitive, point: Vector3, out: Vector3): Vector3 {
    const e = this.byPrim.get(prim);
    if (e === undefined) return out.set(0, 0, 0);
    _pt[0] = point.x;
    _pt[1] = point.y;
    _pt[2] = point.z;
    moverVelocityAt(e.spec, this.clock, _pt, _v);
    return out.set(_v[0], _v[1], _v[2]);
  }

  /**
   * Carry a world point by `prim`'s delta transform for **this** tick, in place.
   *
   * Not velocity × dt: the exact same rotation or translation the geometry just
   * took, so a body planted on a platform holds its contact point forever rather
   * than sliding off over a minute of integration error.
   */
  carryPoint(prim: Primitive, point: Vector3): void {
    const e = this.byPrim.get(prim);
    if (e === undefined) return;
    const spec = e.spec;
    if (spec.type === 'slide') {
      point.x += e.curOffset[0] - e.prevOffset[0];
      point.y += e.curOffset[1] - e.prevOffset[1];
      point.z += e.curOffset[2] - e.prevOffset[2];
      return;
    }
    if (spec.type === 'blink') return; // geometry never moves
    const d = e.curAngle - e.prevAngle;
    if (d === 0) return;
    _axis.set(spec.axis[0], spec.axis[1], spec.axis[2]);
    _quat.setFromAxisAngle(_axis, d);
    _pivot.set(spec.pivot[0], spec.pivot[1], spec.pivot[2]);
    point.sub(_pivot).applyQuaternion(_quat).add(_pivot);
  }

  /**
   * This tick's delta rotation for `prim`, for rotating a rider's contact normal.
   *
   * @returns false (with `outQuat` left identity) when nothing rotated.
   */
  deltaRotation(prim: Primitive, outQuat: Quaternion): boolean {
    const e = this.byPrim.get(prim);
    const spec = e?.spec;
    if (e === undefined || spec === undefined) {
      outQuat.identity();
      return false;
    }
    const d = e.curAngle - e.prevAngle;
    if (d === 0 || (spec.type !== 'orbit' && spec.type !== 'pendulum')) {
      outQuat.identity();
      return false;
    }
    _axis.set(spec.axis[0], spec.axis[1], spec.axis[2]);
    outQuat.setFromAxisAngle(_axis, d);
    return true;
  }

  /** False only for a `blink` primitive inside its off window. */
  isActive(prim: Primitive): boolean {
    const e = this.byPrim.get(prim);
    return e === undefined ? true : e.active;
  }

  /** Whether this system owns `prim`. */
  has(prim: Primitive): boolean {
    return this.byPrim.has(prim);
  }

  // --- broadphase ----------------------------------------------------------

  /** Every primitive that moves, in level order. */
  dynamicPrims(): readonly Primitive[] {
    return this.movingPrims;
  }

  /**
   * Swept bounds of every mover: a conservative superset of every pose it will
   * ever hold, so the broadphase can register it once and never rebuild.
   */
  dynamicBounds(): readonly MoverBounds[] {
    return this.entries;
  }

  // --- render --------------------------------------------------------------

  /** Rest pose and spec of every mover, for a consumer that poses it itself. */
  dynamicEntries(): readonly MoverEntry[] {
    return this.entries;
  }

  /**
   * The clock a frame should be drawn at.
   *
   * The body interpolates `prevPos → pos` on `alpha`, i.e. `alpha = 1` is the
   * last substep and `alpha = 0` is the one before it. A platform sampled at the
   * raw clock would run a whole substep ahead of the rider standing on it.
   */
  renderTime(alpha: number): number {
    return this.clock + (alpha - 1) * TUNING.FIXED_DT;
  }
}
